import { ApiResponse } from '@lunit/shared-types';
import toast from 'react-hot-toast';
import { coreRuntime } from '@core/runtime';
import { ApiError } from '@core/api/apiError';

// Единый HTTP-клиент ядра. Всё шелл-специфичное (слаг, auth-заголовки, credentials,
// реакция на 401) приходит из CoreRuntime — см. runtime.ts.
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface RequestOptions extends RequestInit {
  silent?: boolean;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const { silent = false, ...fetchOptions } = options;
  const rt = coreRuntime();

  try {
    const send = () =>
      fetch(url, {
        ...(rt.credentials ? { credentials: rt.credentials } : {}),
        ...fetchOptions,
        headers: {
          'Content-Type': 'application/json',
          'X-Venue-Slug': rt.getVenueSlug(),
          ...rt.getAuthHeaders(),
          ...fetchOptions.headers,
        },
      });

    let response = await send();

    // Сессия не получена или истекла: шелл входит заново (mini-app — свежая initData) и запрос
    // повторяется один раз с новыми заголовками. Без этого временный сбой входа оставлял клиента
    // без профиля и баллов до перезапуска мини-аппа (K-002: клиент — только из сессии).
    if (response.status === 401 && rt.reauthenticate && (await rt.reauthenticate())) {
      response = await send();
    }

    if (response.status === 401) {
      rt.onUnauthorized?.();
    }

    let data: ApiResponse<T>;
    try {
      data = await response.json();
    } catch {
      const errorMessage = 'Неверный формат ответа от сервера.';
      if (!silent) {
        toast.error(errorMessage);
      }
      throw new ApiError(errorMessage, 'JSON_PARSE_ERROR', response.status);
    }

    if (!data.success || !response.ok) {
      const errorMessage = data.error?.message || 'Что-то пошло не так. Попробуйте снова.';
      const errorCode = data.error?.code || 'UNKNOWN_ERROR';

      // На 401 не спамим тостом — «сессия истекла» обрабатывает шелл (onUnauthorized).
      if (!silent && response.status !== 401) {
        toast.error(errorMessage);
      }

      throw new ApiError(errorMessage, errorCode, response.status);
    }

    return data.data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    const errorMessage = error instanceof Error && error.message.includes('fetch')
      ? 'Ошибка сети. Проверьте подключение к интернету.'
      : 'Что-то пошло не так. Попробуйте снова.';

    if (!silent) {
      toast.error(errorMessage);
    }

    throw new ApiError(errorMessage, 'NETWORK_ERROR');
  }
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) => request<T>(endpoint, options),
  post: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    }),
  put: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    }),
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { method: 'DELETE', ...options }),
};

export { ApiError };
